// src/modules/genai/VAELab.jsx
import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, ScatterChart, Scatter, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

import { _livePalette as P } from '../../context/paletteSync';
const Btn=({onClick,children,color='#22d3ee',disabled,style})=>(
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl=({label,value,min,max,step=0.01,onChange,unit='',color='#22d3ee'})=>(
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))} style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

const randn=()=>{let u=0,v=0;while(!u)u=Math.random();while(!v)v=Math.random();return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v);};
const klDiv=(mu,logvar)=>-0.5*(1+logvar-mu**2-Math.exp(logvar));

// Reparameterization trick: z = mu + eps * sigma
const reparam=(mu,logvar)=>mu+Math.exp(0.5*logvar)*randn();

// Simulate VAE training
const simVAE=({epochs,latentDim,beta,lr})=>{
  const pts=[]; let reconLoss=2.5,klLoss=0.0,totalLoss=2.5;
  for(let e=0;e<epochs;e++){
    const progress=e/epochs;
    reconLoss=Math.max(0.05,2.5*Math.exp(-lr*3*e/10)+0.08+randn()*0.04);
    klLoss=Math.min(1.0,progress*0.8+randn()*0.02);
    totalLoss=reconLoss+beta*klLoss;
    if(e%Math.max(1,Math.floor(epochs/80))===0)
      pts.push({epoch:e,reconLoss:+reconLoss.toFixed(4),klLoss:+klLoss.toFixed(4),totalLoss:+totalLoss.toFixed(4),elbo:+(-totalLoss).toFixed(4)});
  }
  return pts;
};

// Generate 2D latent space clusters
const genLatentClusters=(n,numClasses=5)=>{
  const centers=[{x:0,y:0},{x:2,y:1},{x:-1.5,y:2},{x:1.5,y:-2},{x:-2,y:-1}];
  return Array.from({length:n},(_,i)=>{
    const cls=i%numClasses;
    const c=centers[cls];
    return{x:+(c.x+randn()*0.6).toFixed(3),y:+(c.y+randn()*0.6).toFixed(3),cls};
  });
};

// VAE architecture SVG
function VAEArchSVG({mu, logvar, z, beta}) {
  const W=700,H=180;
  const sigma=Math.exp(0.5*logvar);
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Input */}
      <rect x={10} y={65} width={80} height={50} rx={4} fill="#22d3ee18" stroke="#22d3ee" strokeWidth={1.5}/>
      <text x={50} y={87} textAnchor="middle" style={{fontSize:'9px',fill:'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700}}>INPUT x</text>
      <text x={50} y={100} textAnchor="middle" style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>image/data</text>
      <line x1={90} y1={90} x2={120} y2={90} stroke="#22d3ee44" strokeWidth={2}/>
      <polygon points="120,86 128,90 120,94" fill="#22d3ee44"/>

      {/* Encoder */}
      <rect x={128} y={35} width={110} height={110} rx={4} fill="#22d3ee0a" stroke="#22d3ee" strokeWidth={1.5}/>
      <text x={183} y={82} textAnchor="middle" style={{fontSize:'10px',fill:'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700}}>ENCODER</text>
      <text x={183} y={95} textAnchor="middle" style={{fontSize:'8px',fill:'#94a3b8',fontFamily:'JetBrains Mono'}}>q(z|x)</text>
      <text x={183} y={108} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>FC/Conv layers</text>
      <line x1={238} y1={75} x2={268} y2={65} stroke="#22d3ee44" strokeWidth={1.5}/>
      <line x1={238} y1={105} x2={268} y2={115} stroke="#22d3ee44" strokeWidth={1.5}/>

      {/* mu and logvar */}
      <rect x={268} y={45} width={80} height={30} rx={3} fill="#4ade8015" stroke="#4ade80" strokeWidth={1}/>
      <text x={308} y={58} textAnchor="middle" style={{fontSize:'9px',fill:'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700}}>μ = {mu.toFixed(2)}</text>
      <text x={308} y={69} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>mean</text>
      <rect x={268} y={95} width={80} height={30} rx={3} fill="#fbbf2415" stroke="#fbbf24" strokeWidth={1}/>
      <text x={308} y={108} textAnchor="middle" style={{fontSize:'9px',fill:'#fbbf24',fontFamily:'JetBrains Mono',fontWeight:700}}>σ² = {sigma.toFixed(2)}</text>
      <text x={308} y={119} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>variance</text>

      {/* Reparameterization */}
      <rect x={368} y={65} width={100} height={50} rx={3} fill="#a78bfa15" stroke="#a78bfa" strokeWidth={1.5}/>
      <text x={418} y={83} textAnchor="middle" style={{fontSize:'8px',fill:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700}}>REPARAM</text>
      <text x={418} y={96} textAnchor="middle" style={{fontSize:'7px',fill:'#a78bfa',fontFamily:'JetBrains Mono'}}>z=μ+ε·σ</text>
      <text x={418} y={107} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>ε~N(0,1)</text>
      <line x1={348} y1={60} x2={368} y2={80} stroke="#4ade8044" strokeWidth={1.5}/>
      <line x1={348} y1={110} x2={368} y2={100} stroke="#fbbf2444" strokeWidth={1.5}/>
      <line x1={468} y1={90} x2={498} y2={90} stroke="#a78bfa44" strokeWidth={2}/>
      <polygon points="498,86 506,90 498,94" fill="#a78bfa44"/>

      {/* z */}
      <rect x={506} y={68} width={65} height={44} rx={3} fill="#a78bfa22" stroke="#a78bfa" strokeWidth={1.5}/>
      <text x={538} y={87} textAnchor="middle" style={{fontSize:'10px',fill:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700}}>z</text>
      <text x={538} y={100} textAnchor="middle" style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{z.toFixed(2)}</text>
      <line x1={571} y1={90} x2={598} y2={90} stroke="#a78bfa44" strokeWidth={2}/>
      <polygon points="598,86 606,90 598,94" fill="#a78bfa44"/>

      {/* Decoder */}
      <rect x={606} y={55} width={80} height={70} rx={4} fill="#f8717115" stroke="#f87171" strokeWidth={1.5}/>
      <text x={646} y={82} textAnchor="middle" style={{fontSize:'9px',fill:'#f87171',fontFamily:'JetBrains Mono',fontWeight:700}}>DECODER</text>
      <text x={646} y={94} textAnchor="middle" style={{fontSize:'8px',fill:'#94a3b8',fontFamily:'JetBrains Mono'}}>p(x|z)</text>
      <text x={646} y={106} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>x̂ = G(z)</text>

      {/* KL label */}
      <text x={308} y={160} textAnchor="middle" style={{fontSize:'8px',fill:'#fbbf24',fontFamily:'JetBrains Mono'}}>KL[q(z|x)||p(z)] → regularize z~N(0,1)</text>
      <text x={175} y={172} textAnchor="middle" style={{fontSize:'8px',fill:'#f87171',fontFamily:'JetBrains Mono'}}>β={beta} × KL</text>
    </svg>
  );
}

const TABS=['architecture','training','latent-space','reconstruction','vae-vs-gan'];

export default function VAELab() {
  const [tab,setTab]=useState('architecture');
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'VAE Lab — learn a smooth, structured latent space with probabilistic encoding.',t:'info'}]);
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [latentDim,setLatentDim]=useState(2);
  const [beta,setBeta]=useState(1.0);
  const [lr,setLr]=useState(0.001);
  const [epochs,setEpochs]=useState(150);
  const [result,setResult]=useState(null);
  const [running,setRunning]=useState(false);
  const [sampleMu,setSampleMu]=useState(0.0);
  const [sampleLogvar,setSampleLogvar]=useState(0.0);
  const [latentPts,setLatentPts]=useState(null);
  const [walkStep,setWalkStep]=useState(0);
  const [walking,setWalking]=useState(false);
  const walkRef=useRef();

  const run=()=>{
    setRunning(true);
    setTimeout(()=>{
      const pts=simVAE({epochs,latentDim,beta,lr});
      setResult(pts);
      const lp=genLatentClusters(200);
      setLatentPts(lp);
      log(`VAE trained: β=${beta} dim=${latentDim} lr=${lr} | final ELBO=${pts.at(-1)?.elbo}`,'ok');
      setRunning(false);
    },30);
  };

  const toggleWalk=()=>{
    if(walking){clearInterval(walkRef.current);setWalking(false);}
    else{setWalking(true);walkRef.current=setInterval(()=>setWalkStep(s=>s+1),300);}
  };
  useEffect(()=>()=>clearInterval(walkRef.current),[]);

  const mu=sampleMu+Math.sin(walkStep*0.3)*0.4*walking;
  const logvar=sampleLogvar+Math.cos(walkStep*0.2)*0.3*walking;
  const z=reparam(mu,logvar);
  const last=result?.at(-1);
  const classColors=['#22d3ee','#f87171','#4ade80','#a78bfa','#fbbf24'];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'#22d3ee':'transparent'}`,background:tab===t?'#22d3ee18':'transparent',color:tab===t?'#22d3ee':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr auto',gap:'10px',padding:'12px',background:'var(--surface)',border:'1px solid var(--b0)',borderRadius:'4px',alignItems:'end'}}>
        <Sl label="Latent dim" value={latentDim} min={1} max={32} step={1} onChange={setLatentDim} color="#22d3ee"/>
        <Sl label="β (KL weight)" value={beta} min={0.1} max={10} step={0.1} onChange={setBeta} color="#fbbf24"/>
        <Sl label="Learning Rate" value={lr} min={0.0001} max={0.01} step={0.0001} onChange={setLr} color="#4ade80"/>
        <Sl label="Epochs" value={epochs} min={50} max={400} step={10} onChange={setEpochs} color="#a78bfa"/>
        <Btn onClick={run} disabled={running} color="#22d3ee">{running?'TRAINING…':'TRAIN →'}</Btn>
      </div>

      {tab==='architecture'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="VAE ARCHITECTURE — ENCODE TO DISTRIBUTION, SAMPLE, DECODE" accent="#22d3ee">
            <VAEArchSVG mu={mu} logvar={logvar} z={z} beta={beta}/>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
            <Panel title="ENCODER q(z|x)" accent="#22d3ee">
              {[['Input','Data x (image, text, etc)'],['Outputs','μ(x) and log σ²(x)'],['Meaning','z distribution given x'],['Key','Not a point — a Gaussian!']].map(([k,v])=>(
                <div key={k} style={{fontSize:'9px',padding:'5px',background:'var(--deep)',borderRadius:'2px',marginBottom:'4px',borderLeft:'2px solid #22d3ee'}}><span style={{color:'var(--t3)',marginRight:6}}>{k}:</span><span style={{color:'var(--t2)'}}>{v}</span></div>
              ))}
            </Panel>
            <Panel title="REPARAMETERIZATION TRICK" accent="#a78bfa">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8,marginBottom:'6px'}}>Allows gradients to flow through sampling:</div>
              <div style={{padding:'8px',background:'var(--deep)',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'10px',color:'#a78bfa',marginBottom:'6px'}}>z = μ + ε·σ, ε~N(0,1)</div>
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>ε is the randomness. μ and σ are deterministic → gradients flow through them. Without this: backprop stops at sampling.</div>
            </Panel>
            <Panel title="DECODER p(x|z)" accent="#f87171">
              {[['Input','Sampled z vector'],['Output','Reconstructed x̂'],['Loss','||x − x̂||² (MSE or BCE)'],['Goal','p(x|z) as accurate as possible'],['Constraint','Must work for any z~N(0,1)']].map(([k,v])=>(
                <div key={k} style={{fontSize:'9px',padding:'5px',background:'var(--deep)',borderRadius:'2px',marginBottom:'4px',borderLeft:'2px solid #f87171'}}><span style={{color:'var(--t3)',marginRight:6}}>{k}:</span><span style={{color:'var(--t2)'}}>{v}</span></div>
              ))}
            </Panel>
          </div>
        </div>
      )}

      {tab==='training'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {result?(<>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
              <Metric label="Recon Loss" value={last?.reconLoss} color="#22d3ee"/>
              <Metric label="KL Loss" value={last?.klLoss} color="#fbbf24"/>
              <Metric label="Total Loss" value={last?.totalLoss} color="#f87171"/>
              <Metric label="ELBO" value={last?.elbo} color="#4ade80"/>
            </div>
            <Panel title="ELBO = Reconstruction Loss + β·KL Divergence" accent="#22d3ee">
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>ELBO = Evidence Lower Bound. Maximize ELBO = minimize −log p(x) − KL. β controls how tight the latent space is.</div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={result}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/><YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <Tooltip/><Legend/>
                  <Line type="monotone" dataKey="reconLoss" stroke="#22d3ee" strokeWidth={2} dot={false} name="Recon Loss"/>
                  <Line type="monotone" dataKey="klLoss" stroke="#fbbf24" strokeWidth={2} dot={false} name="KL Loss"/>
                  <Line type="monotone" dataKey="totalLoss" stroke="#f87171" strokeWidth={2} dot={false} name="Total Loss"/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="β EFFECT — β={beta} | HIGH β = SMOOTHER SPACE, WORSE RECON" accent="#fbbf24">
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                {[{b:0.1,d:'Very low β: sharp recon, messy latent space. Holes between classes.',c:'#f87171'},{b:1.0,d:'β=1: standard VAE. Balanced recon and disentanglement.',c:'#fbbf24'},{b:5.0,d:'High β (β-VAE): very smooth, structured space. Each dim meaningful.',c:'#4ade80'}].map(({b,d,c})=>(
                  <div key={b} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${c}`,opacity:Math.abs(beta-b)<2?1:0.5}}>
                    <div style={{fontSize:'11px',color:c,fontFamily:'Syne',fontWeight:700,marginBottom:'4px'}}>β={b}</div>
                    <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{d}</div>
                  </div>
                ))}
              </div>
            </Panel>
          </>):<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Train the VAE first</div>}
          <LogPanel logs={logs} height={70}/>
        </div>
      )}

      {tab==='latent-space'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="2D LATENT SPACE — ENCODED CLUSTERS BY CLASS" accent="#a78bfa">
            {latentPts?(<>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>Each color = a class. In a well-trained VAE, classes cluster smoothly with overlapping regions — no holes.</div>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="x" type="number" tick={{fontSize:9,fill:'var(--t3)'}} name="z₁"/>
                  <YAxis dataKey="y" type="number" tick={{fontSize:9,fill:'var(--t3)'}} name="z₂"/>
                  <Tooltip cursor={{strokeDasharray:'3 3'}}/>
                  {[0,1,2,3,4].map(cls=>(
                    <Scatter key={cls} name={`Class ${cls}`} data={latentPts.filter(p=>p.cls===cls)} fill={classColors[cls]} opacity={0.75}/>
                  ))}
                </ScatterChart>
              </ResponsiveContainer>
            </>):<div style={{height:200,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'12px'}}>← Train VAE to visualize latent space</div>}
          </Panel>
          <Panel title="SAMPLE FROM LATENT SPACE — CONTROL μ AND σ" accent="#22d3ee">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr auto',gap:'12px',alignItems:'end'}}>
              <Sl label="μ (mean)" value={sampleMu} min={-3} max={3} step={0.1} onChange={setSampleMu} color="#4ade80"/>
              <Sl label="log σ²" value={sampleLogvar} min={-3} max={2} step={0.1} onChange={setSampleLogvar} color="#fbbf24"/>
              <Btn onClick={toggleWalk} color={walking?'#f87171':'#22d3ee'}>{walking?'STOP':'WALK z-SPACE →'}</Btn>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginTop:'8px'}}>
              <Metric label="μ" value={mu.toFixed(3)} color="#4ade80"/>
              <Metric label="σ" value={Math.exp(0.5*logvar).toFixed(3)} color="#fbbf24"/>
              <Metric label="z (sampled)" value={z.toFixed(3)} color="#a78bfa"/>
              <Metric label="KL(q||p)" value={klDiv(mu,logvar).toFixed(4)} color="#f87171"/>
            </div>
          </Panel>
        </div>
      )}

      {tab==='reconstruction'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="RECONSTRUCTION QUALITY — LATENT DIM vs BETA TRADEOFF" accent="#4ade80">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={[1,2,4,8,16,32].map(d=>({dim:d,quality:+(1-0.6*Math.exp(-d*0.3)).toFixed(3),smoothness:+(1-d/40).toFixed(3)}))}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="dim" tick={{fontSize:10,fill:'var(--t3)'}} label={{value:'latent dim',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
                <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                <Tooltip/><Legend/>
                <Line type="monotone" dataKey="quality" stroke="#4ade80" strokeWidth={2} dot={{r:4}} name="Recon quality (↑ better)"/>
                <Line type="monotone" dataKey="smoothness" stroke="#a78bfa" strokeWidth={2} dot={{r:4}} name="Latent smoothness (↑ better)"/>
              </LineChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="RECONSTRUCTION vs GENERATION" accent="#fbbf24">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div>
                <div style={{fontSize:'10px',color:'#fbbf24',fontWeight:700,marginBottom:'8px'}}>RECONSTRUCTION (encode→decode)</div>
                {['Pass real x through encoder → get μ,σ','Sample z ~ N(μ,σ)','Decode z → x̂ ≈ x (reconstruction)','Loss: ||x − x̂||² + β·KL','Goal: reproduce input accurately'].map((t,i)=>(
                  <div key={i} style={{fontSize:'9px',padding:'4px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px',borderLeft:'2px solid #fbbf24',color:'var(--t2)'}}>{t}</div>
                ))}
              </div>
              <div>
                <div style={{fontSize:'10px',color:'#22d3ee',fontWeight:700,marginBottom:'8px'}}>GENERATION (sample from prior)</div>
                {['Sample z ~ N(0,I) directly','No encoder needed','Decode z → x̂ (new sample)','Only works well if KL was enforced','Goal: produce plausible novel samples'].map((t,i)=>(
                  <div key={i} style={{fontSize:'9px',padding:'4px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px',borderLeft:'2px solid #22d3ee',color:'var(--t2)'}}>{t}</div>
                ))}
              </div>
            </div>
          </Panel>
        </div>
      )}

      {tab==='vae-vs-gan'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="VAE vs GAN — FUNDAMENTAL TRADEOFFS" accent="#22d3ee">
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'10px'}}>
              <thead><tr style={{background:'var(--deep)'}}>
                {['','VAE','GAN'].map(h=><th key={h} style={{padding:'8px 12px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {[
                  ['Training','Stable (max ELBO)','Unstable (adversarial)'],
                  ['Image quality','Blurry (MSE loss)','Sharp (adversarial loss)'],
                  ['Latent space','Smooth, structured','No guarantee'],
                  ['Interpolation','Excellent','Good (DCGAN+)'],
                  ['Mode collapse','No','Yes (vanilla/DCGAN)'],
                  ['Inference','Has encoder: q(z|x)','No encoder by default'],
                  ['Theory','Variational inference','Game theory'],
                  ['Best for','Structure, control, VAE-edit','High-quality image gen'],
                ].map(([feat,vae,gan],i)=>(
                  <tr key={feat} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'var(--deep)':'transparent'}}>
                    <td style={{padding:'7px 12px',color:'var(--t3)',fontWeight:700,fontSize:'9px'}}>{feat}</td>
                    <td style={{padding:'7px 12px',color:'#22d3ee',fontSize:'9px',fontFamily:'JetBrains Mono'}}>{vae}</td>
                    <td style={{padding:'7px 12px',color:'#f87171',fontSize:'9px',fontFamily:'JetBrains Mono'}}>{gan}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
            {[
              {name:'VAE',c:'#22d3ee',use:'Editing, interpolation, structured latent space, anomaly detection',ex:'Face editing, drug discovery, DALL-E (hybrid)'},
              {name:'GAN',c:'#f87171',use:'Photorealistic generation, super-resolution, data augmentation',ex:'StyleGAN faces, DeepFake, Pix2Pix, CycleGAN'},
              {name:'VAE-GAN hybrid',c:'#a78bfa',use:'Best of both: structured + sharp. Adversarial recon loss.',ex:'DALL-E 1 (VAE + CLIP + AR), VQGAN, Stable Diffusion encoder'},
            ].map(({name,c,use,ex})=>(
              <Panel key={name} title={name} accent={c}>
                <div style={{fontSize:'9px',marginBottom:'5px'}}><span style={{color:'var(--t3)'}}>Best for: </span><span style={{color:c}}>{use}</span></div>
                <div style={{fontSize:'9px'}}><span style={{color:'var(--t3)'}}>Examples: </span><span style={{color:'var(--t2)'}}>{ex}</span></div>
              </Panel>
            ))}
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
